//@flow

import { graphql, Link, useStaticQuery } from "gatsby";
import React from "react";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";

import type { SectionLink } from "@/components/FooterLinksSection/FooterLinksSection";
import FooterLinksSection from "@/components/FooterLinksSection/FooterLinksSection";
import FooterSocialIcons from "@/components/FooterSocialIcons/FooterSocialIcons";
import TwinbruLogoWithText from "@/images/BruFabrics-logo.inline.svg";

type Section = {
    heading: {
        path?: string,
        content: string,
    },
    links: SectionLink[],
};

/**
 * The list of sections to be rendered in the footer.
 *
 * @type {Section[]}
 */
const footerSections: Section[] = [
    {
        heading: {
            path: "/products-and-brands",
            content: "Products & Brands",
        },
        links: [
            {
                path: "/products-and-brands/bru-fabrics",
                content: "Bru Fabrics",
            },
            {
                path: "/products-and-brands/fibre-guard",
                content: "FibreGuard",
            },
            {
                path: "/products-and-brands/fr-one",
                content: "FR-One",
            },
            {
                path: "/products-and-brands/twinbru",
                content: "Twinbru",
            },
            {
                path: "/products-and-brands/products",
                content: "Products",
            },
        ],
    },
    {
        heading: {
            path: "/solutions",
            content: "Solutions",
        },
        links: [
            {
                path: "/solutions#design-studio",
                content: "Design Studio",
            },
            {
                path: "/solutions#lab",
                content: "Textiles Lab",
            },
            {
                path: "/solutions#supply-chain",
                content: "Supply Chain",
            },
            {
                path: "/solutions#sampling-solutions",
                content: "Sampling Solutions",
            },
            {
                path: "/solutions#visualisation",
                content: "Visualisation Service",
            },
            {
                path: "/solutions#api",
                content: "API",
            },
            {
                path: "/solutions#support-and-sales",
                content: "Support",
            },
        ],
    },
    {
        heading: {
            path: "/customers",
            content: "Customers",
        },
        links: [
            {
                path: "/customers#distribution-brands",
                content: "Distribution",
            },
            {
                path: "/customers#manufacturers",
                content: "Manufacturers",
            },
        ],
    },
    {
        heading: {
            path: "/dna",
            content: "DNA",
        },
        links: [
            {
                path: "/dna#values",
                content: "Our DNA",
            },
            {
                path: "/sustainability",
                content: "Sustainability",
            },
            {
                path: "/social-investments",
                content: "Social Investments",
            },
            {
                path: "/innovation",
                content: "Innovations",
            },
            {
                path: "/quality",
                content: "Quality",
            },
            {
                path: "https://jobs.brutex.com/",
                content: "Jobs",
            },
        ],
    },
    {
        heading: {
            path: "/group",
            content: "Group",
        },
        links: [
            {
                path: "/group#distribution",
                content: "Distribution Partners",
            },
            {
                path: "/group#supporting",
                content: "Supporting Partners",
            },
            {
                path: "/group#affiliate",
                content: "Affiliate Partners",
            },
            {
                path: "/group#technology",
                content: "Technology Partners",
            },
        ],
    },
    {
        heading: {
            path: "/privacy-statement",
            content: "Legal",
        },
        links: [
            {
                path: "/privacy-statement",
                content: "Privacy Statement",
            },
            {
                path: "/disclaimer",
                content: "Disclaimer",
            },
            {
                path: "/cookie-policy",
                content: "Cookie Policy",
            },
            {
                path: "/general-terms",
                content: "General Terms and Conditions",
            },
            {
                path: () => {
                    if ("ppms" in window) {
                        window.ppms.cm.api("openConsentForm");
                    }
                },
                content: "Manage Privacy Settings",
            },
        ],
    },
];

const yearNow = new Date().getFullYear();

const Footer = (): React$Element<"footer"> => {
    const sections = footerSections.map((section: Section) => (
        <Col
            key={section.heading.content}
            sm={6}
            md={4}
            className={"footer-nav-col"}
        >
            <FooterLinksSection
                links={section.links}
                heading={section.heading}
            />
        </Col>
    ));

    const { contact } = useStaticQuery(graphql`
        query {
            contact {
                ...BruContactDetails
            }
        }
    `);

    const { headOffice, info, social } = contact;

    return (
        <footer>
            <div className="container">
                <Row className="content fs-2">
                    <Col xl={3} className="pb-5 pb-xl-0">
                        <Link to="/">
                            <div className="svg-box icon-logo">
                                <div className="svg-container">
                                    <TwinbruLogoWithText className="brand" />
                                </div>
                            </div>
                        </Link>
                    </Col>

                    <Col xl={9}>
                        <Row>{sections}</Row>
                    </Col>
                </Row>

                <Row className="pb-4 mb-2 text-muted fs-2">
                    <Col lg={9}>
                        <ul className={"list-inline list-unstyled mb-0"}>
                            <li className={"list-inline-item"}>
                                Copyright © {yearNow} Bru. All rights reserved.
                            </li>
                        </ul>
                        <ul className={"list-inline list-unstyled mb-0"}>
                            <li className={"list-inline-item"}>
                                Head office: {headOffice.name},{" "}
                                {headOffice.street} {headOffice.streetNumber},{" "}
                                {headOffice.postalCode} {headOffice.city},{" "}
                                {headOffice.country}
                            </li>

                            <li className={"list-inline-item"}>
                                <a href={`tel:${info.phone.raw}`}>
                                    {info.phone.formatted}
                                </a>
                                <span className="d-lg-none"> | </span>
                            </li>
                        </ul>
                    </Col>

                    <Col lg={3} className="social-links mt-3 mt-lg-0">
                        <div className="wrap float-lg-right">
                            <FooterSocialIcons links={social} />
                        </div>
                    </Col>
                </Row>
            </div>
        </footer>
    );
};

export default Footer;
